//Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import React, { ReactElement } from 'react'

import { Box, Grid, Typography } from '@mui/material'
import { OtStoreState, useOtChainState, useOtSelector } from '@react/redux/store'
import { PoolLytOverviewCTO, PoolLytOverviewCTOSchema } from '@shared/sharedTypes'

import { FileCopy } from '@mui/icons-material'
import IconButton from '@mui/material/IconButton'
import { otAssert } from '@shared/conversions'
import { RoleEnum } from '@shared/Enums'
import { ETH_ADDR_ZERO } from '@shared/SchemaTypes'
import { SchemaDescription } from 'yup'

interface ContractAddrListItem {
  label: string
  value: string
}

export default function LytPoolContractAddresses({ role }: { role: RoleEnum }): React.ReactElement {
  const {
    stageConfiguration: { lytPoolAddr },
    chainConfiguration: { perimeterAddresses },
  } = useOtChainState()

  const poolLytOverviewCTO = useOtSelector(
    (state: OtStoreState) => state.poolState.poolOverviewCTOs[lytPoolAddr ?? ETH_ADDR_ZERO]
  ) as PoolLytOverviewCTO | null

  otAssert(perimeterAddresses, 'perimeterAddresses is not defined')

  const handleCopyClick = (text: string): void => {
    navigator.clipboard.writeText(text).catch((_e) => {})
  }

  const contractAddresses: ContractAddrListItem[] = [
    {
      label: 'Lyt Token Address',
      value: poolLytOverviewCTO?.poolAddr ?? '',
    },
    {
      label: 'USDC Address',
      value: poolLytOverviewCTO?.liquidityAssetAddr ?? '',
    },
  ]

  if (role !== RoleEnum.LENDER && poolLytOverviewCTO) {
    contractAddresses.push({
      label: (PoolLytOverviewCTOSchema.describe().fields.poolAdminAddr as SchemaDescription).label ?? '',
      value: poolLytOverviewCTO.poolAdminAddr ?? '',
    })
    contractAddresses.push({
      label: 'Lyt Issuer Address',
      value: poolLytOverviewCTO.borrowerManagerAddr ?? '',
    })
    contractAddresses.push({
      label: 'Issuer Vault Wallet',
      value: poolLytOverviewCTO.treasuryWalletAddr ?? '',
    })
    contractAddresses.push({
      label: 'Lyt Fee Collector',
      value: poolLytOverviewCTO.feeCollectorAddress ?? '',
    })
    contractAddresses.push({
      label: (PoolLytOverviewCTOSchema.describe().fields.poolControllerAddr as SchemaDescription).label ?? '',
      value: poolLytOverviewCTO.poolControllerAddr ?? '',
    })
  }

  const gridItems: ReactElement[] = []
  contractAddresses.forEach((addr, index) => {
    gridItems.push(
      <Grid item xs={4} key={addr.label + ' ' + index}>
        <Typography variant="body1" align={'left'} component="div" sx={{ paddingTop: '5px', flexGrow: 1 }}>
          {addr.label}
        </Typography>
      </Grid>
    )
    gridItems.push(
      <Grid item xs={8} key={addr.label + ' ' + index + '2'}>
        <div
          style={{
            justifyContent: 'center',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <Typography variant="body1" component="div" sx={{ flexGrow: 1 }} align="left">
            {addr.value}
          </Typography>
          <IconButton
            onClick={() => {
              handleCopyClick(addr.value)
            }}
          >
            <FileCopy />
          </IconButton>
        </div>
      </Grid>
    )
  })

  return (
    <Box>
      <Grid container spacing={1}>
        {gridItems}
      </Grid>
    </Box>
  )
}
